import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useNavigate, useParams } from "react-router-dom";
import { activityApi } from "../../api/activity";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectCapabilityEnabled } from "../../utils/projectCapabilities";
import { normalizeProjectModules } from "../../utils/projectModules";
import {
  activityActorLabel,
  activityDisplayDescription,
  activityDisplayTitle,
  activityEntityDestination,
  activityJourneyStepLabel,
  groupActivityEvents,
} from "../../utils/activityPresentation";
import EditorialIntro from "../../components/dashboard/EditorialIntro";
import ResponsiveListControls from "../../components/dashboard/ResponsiveListControls";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { useListScrollRestoration, usePersistentListValue } from "../../utils/listViewState";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };
const PAGE_SIZE = 40;
const CATEGORY_LABELS = {
  navigation: "Navegación",
  commercial: "Comercial",
  auth: "Accesos",
  user: "Usuarios",
  customer: "Clientes",
  project: "Proyecto",
  landing: "Página",
  product: "Productos / servicios",
  order: "Pedidos",
  payment: "Pagos",
  appointment: "Agenda",
  erp: "ERP",
  cash: "Caja",
  stock: "Stock",
  billing: "Billing",
  push: "Notificaciones",
  integration: "Integraciones",
  membership: "Membresías",
  system: "Sistema",
};
const SOURCE_LABELS = {
  storefront: "Web pública",
  panel: "Panel",
  webhook: "Automatización",
  api: "API / sistema",
  system: "Sistema",
  job: "Proceso automático",
};
const ACTOR_TYPE_LABELS = {
  user: "Equipo",
  visitor: "Visitante web",
  customer: "Cliente registrado",
  system: "Sistema",
  webhook: "Automatización",
};
const PERIODS = {
  "24h": { label: "Últimas 24 h", hours: 24 },
  "7d": { label: "Últimos 7 días", hours: 24 * 7 },
  "30d": { label: "Últimos 30 días", hours: 24 * 30 },
  "90d": { label: "Últimos 90 días", hours: 24 * 90 },
  all: { label: "Todo el historial", hours: 0 },
};

function dateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function periodParams(periodKey) {
  const period = PERIODS[periodKey] || PERIODS["7d"];
  if (!period.hours) return {};
  const to = new Date();
  const from = new Date(to.getTime() - period.hours * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function sourceLabel(source) {
  return SOURCE_LABELS[source] || source || "Origen desconocido";
}

function Metric({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
      <CardContent sx={{ p: { xs: 1.25, sm: 2.1 }, "&:last-child": { pb: { xs: 1.25, sm: 2.1 } } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: .65, sm: 1.25 }} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Box sx={{ width: { xs: 32, sm: 42 }, height: { xs: 32, sm: 42 }, borderRadius: 2.3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange, "& svg": { fontSize: { xs: 18, sm: 23 } } }}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: "block", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 900, lineHeight: 1.2 }}>{title}</Typography>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: 22, sm: 28 }, lineHeight: 1.1, mt: .15 }}>{value}</Typography>
            <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", mt: .2 }}>{helper}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProjectActivityPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { projects, activeProject } = useProjects();
  const effectiveProject = React.useMemo(() => {
    const matches = (project) => [project?.slug, project?.id, project?._id]
      .filter(Boolean)
      .some((value) => String(value) === String(projectId));
    return (projects || []).find(matches) || (matches(activeProject) ? activeProject : null);
  }, [projects, activeProject, projectId]);
  const membershipsEnabled = isProjectCapabilityEnabled(effectiveProject, "memberships");
  const modules = normalizeProjectModules(effectiveProject?.modules);
  const projectKey = effectiveProject?.slug || effectiveProject?.id || effectiveProject?._id || projectId;
  const encodedProjectKey = encodeURIComponent(projectKey || projectId || "");
  const projectBase = `/dashboard/proyectos/${encodedProjectKey}`;
  const itemPath = modules.system ? `${projectBase}/sistema/items` : `${projectBase}/productos`;

  const [items, setItems] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [journeys, setJourneys] = React.useState([]);
  const [pagination, setPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = usePersistentListValue(`activity:${projectId}:filters`, {
    category: "",
    source: "",
    actorType: "",
    period: "7d",
    q: "",
  });
  const categoryFilterValue = membershipsEnabled || filters.category !== "membership" ? (filters.category || "") : "";
  const [page, setPage] = usePersistentListValue(`activity:${projectId}:page`, 1);
  const [searchDraft, setSearchDraft] = React.useState(filters.q || "");
  const [listLoading, setListLoading] = React.useState(true);
  const [overviewLoading, setOverviewLoading] = React.useState(true);
  const [listError, setListError] = React.useState("");
  const [overviewError, setOverviewError] = React.useState("");
  const listRequestRef = React.useRef(0);
  const listQueryKey = JSON.stringify([projectId, page, filters.category, filters.source, filters.actorType, filters.period, filters.q]);
  const latestListQueryKeyRef = React.useRef(listQueryKey);
  latestListQueryKeyRef.current = listQueryKey;

  React.useEffect(() => {
    setSearchDraft(filters.q || "");
  }, [filters.q, projectId]);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      if ((filters.q || "") === searchDraft) return;
      setPage(1);
      setFilters((current) => ({ ...current, q: searchDraft }));
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [searchDraft, filters.q, setFilters, setPage]);

  const loadOverview = React.useCallback(async () => {
    try {
      setOverviewLoading(true);
      setOverviewError("");
      const [summaryResult, journeysResult] = await Promise.allSettled([
        activityApi.projectSummary(projectId, 24),
        activityApi.projectJourneys(projectId, { hours: 168, limit: 20 }),
      ]);
      if (summaryResult.status === "fulfilled") setSummary(summaryResult.value?.summary || null);
      else setSummary(null);
      if (journeysResult.status === "fulfilled") setJourneys(journeysResult.value?.items || []);
      else setJourneys([]);
      if (summaryResult.status === "rejected" || journeysResult.status === "rejected") {
        setOverviewError("No se pudo actualizar una parte del resumen. El historial sigue disponible.");
      }
    } finally {
      setOverviewLoading(false);
    }
  }, [projectId]);

  const loadList = React.useCallback(async () => {
    const numericPage = Number(page);
    const requestedPage = Number.isFinite(numericPage) && numericPage >= 1 ? Math.floor(numericPage) : 1;

    if (numericPage !== requestedPage) {
      setListLoading(true);
      setListError("");
      setPage(requestedPage);
      return;
    }

    const requestId = ++listRequestRef.current;
    const requestQueryKey = latestListQueryKeyRef.current;
    let correctingPage = false;

    try {
      setListLoading(true);
      setListError("");
      const params = {
        page: requestedPage,
        pageSize: PAGE_SIZE,
        presentation: "human",
        ...periodParams(filters.period),
        ...Object.fromEntries(
          Object.entries({
            category: filters.category,
            source: filters.source,
            actorType: filters.actorType,
            q: filters.q,
          }).filter(([, value]) => value),
        ),
      };
      const response = await activityApi.project(projectId, params);
      if (requestId !== listRequestRef.current || requestQueryKey !== latestListQueryKeyRef.current) return;

      const nextPagination = response.pagination || { page: requestedPage, pages: 1, total: 0 };
      const responsePages = Number(nextPagination.pages);
      const availablePages = Number.isFinite(responsePages) && responsePages >= 1 ? Math.floor(responsePages) : 1;

      if (requestedPage > availablePages) {
        correctingPage = true;
        setPage(availablePages);
        return;
      }

      setItems(response.items || []);
      setPagination(nextPagination);
    } catch (err) {
      if (requestId !== listRequestRef.current || requestQueryKey !== latestListQueryKeyRef.current) return;
      setListError(err.message || "No se pudo cargar el historial de actividad");
      setItems([]);
      setPagination({ page: requestedPage, pages: 1, total: 0 });
    } finally {
      if (
        requestId === listRequestRef.current
        && requestQueryKey === latestListQueryKeyRef.current
        && !correctingPage
      ) {
        setListLoading(false);
      }
    }
  }, [projectId, page, filters.category, filters.source, filters.actorType, filters.period, filters.q, setPage]);

  React.useEffect(() => { loadOverview(); }, [loadOverview]);
  React.useEffect(() => { loadList(); }, [loadList]);

  React.useEffect(() => {
    if (!membershipsEnabled && filters.category === "membership") {
      setFilters((current) => ({ ...current, category: "" }));
      setPage(1);
    }
  }, [membershipsEnabled, filters.category, setFilters, setPage]);

  useListScrollRestoration(`activity:${projectId}`, !listLoading);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const clearFilters = () => {
    setSearchDraft("");
    setFilters((current) => ({ ...current, category: "", source: "", actorType: "", period: "7d", q: "" }));
    setPage(1);
  };

  const groupedItems = React.useMemo(() => groupActivityEvents(items), [items]);
  const refreshAll = () => Promise.allSettled([loadOverview(), loadList()]);
  const activeFilterCount = [filters.category, filters.source, filters.actorType, filters.period !== "7d" ? filters.period : ""]
    .filter(Boolean).length;

  return (
    <Stack spacing={3}>
      <EditorialIntro
        title="Actividad y recorridos"
        titleVariant="h3"
        context="Qué pasó, quién lo hizo y sobre qué parte del negocio."
        helpTitle="Qué incluye este historial"
        helpText="El historial prioriza operaciones humanas y comerciales. La navegación repetitiva se conserva para recorridos y analítica, pero no invade la lista principal."
        action={<Button onClick={refreshAll} disabled={listLoading || overviewLoading} startIcon={<RefreshRoundedIcon />} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Actualizar</Button>}
      />

      {overviewError ? <Alert severity="warning">{overviewError}</Alert> : null}

      <Grid container spacing={{ xs: 1, sm: 2 }}>
        <Grid item xs={6} lg={3}><Metric icon={<HistoryRoundedIcon />} title="Eventos en 24 h" value={overviewLoading && !summary ? "…" : summary?.total ?? "—"} helper="Web y operación" /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<PeopleAltRoundedIcon />} title="Visitantes únicos" value={overviewLoading && !summary ? "…" : summary?.uniqueVisitors ?? "—"} helper="Navegadores anónimos" /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<BoltRoundedIcon />} title="Alta intención" value={overviewLoading && !summary ? "…" : summary?.highIntent ?? "—"} helper="WhatsApp, carrito y checkout" /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<DashboardRoundedIcon />} title="Usuarios del panel" value={overviewLoading && !summary ? "…" : summary?.uniquePanelUsers ?? "—"} helper="Dueños y empleados activos" /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: { xs: 1.35, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
          <RouteRoundedIcon sx={{ color: BRAND.orange }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: 18, sm: 24 } }}>Recorridos recientes</Typography>
            <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>La navegación vive acá como recorrido, no como decenas de líneas del historial.</Typography>
          </Box>
        </Stack>
        <Stack spacing={1}>
          {journeys.slice(0, 10).map((journey) => (
            <Box component="details" key={`${journey.channel}-${journey.sessionId}`} sx={{ p: { xs: 1.1, sm: 1.5 }, borderRadius: 3, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", "&[open] .journey-chevron": { transform: "rotate(180deg)" } }}>
              <Stack component="summary" direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ minHeight: 42, cursor: "pointer", listStyle: "none", "&::-webkit-details-marker": { display: "none" } }}>
                <Stack direction="row" spacing={.65} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                  <Chip size="small" icon={journey.channel === "storefront" ? <StorefrontRoundedIcon /> : <DashboardRoundedIcon />} label={journey.channel === "storefront" ? "Web pública" : "Panel"} />
                  <Chip size="small" variant="outlined" label={`${journey.stepsCount || 0} pasos`} />
                  {journey.highIntent ? <Chip size="small" color="success" label={`${journey.highIntent} comerciales`} /> : null}
                </Stack>
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{dateTime(journey.lastAt)}</Typography>
                  <Typography className="journey-chevron" component="span" sx={{ display: { xs: "inline-block", sm: "none" }, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", transition: "transform .18s ease" }}>⌄</Typography>
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ mt: .85, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)", lineHeight: 1.55, overflowWrap: "anywhere" }}>
                {(journey.steps || []).map((step) => activityJourneyStepLabel(step)).join(" → ") || "Sin pasos visibles"}
              </Typography>
              <Typography variant="caption" sx={{ display: { xs: "block", sm: "none" }, mt: .65, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{dateTime(journey.lastAt)}</Typography>
            </Box>
          ))}
          {!overviewLoading && !journeys.length ? <Alert severity="info">Todavía no hay recorridos registrados para este proyecto.</Alert> : null}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.1, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <ResponsiveListControls
          search={<TextField fullWidth size="small" placeholder="Buscar acción, persona o entidad" inputProps={{ "aria-label": "Buscar actividad, persona o entidad" }} value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} />}
          activeFilterCount={activeFilterCount}
          onClear={clearFilters}
          drawerTitle="Filtrar actividad"
        >
          <FormControl size="small" sx={{ minWidth: { sm: 180 } }}>
            <InputLabel>Categoría</InputLabel>
            <Select label="Categoría" value={categoryFilterValue} onChange={(event) => updateFilter("category", event.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(CATEGORY_LABELS).filter(([key]) => membershipsEnabled || key !== "membership").map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { sm: 160 } }}>
            <InputLabel>Origen</InputLabel>
            <Select label="Origen" value={filters.source || ""} onChange={(event) => updateFilter("source", event.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(SOURCE_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { sm: 170 } }}>
            <InputLabel>Actor</InputLabel>
            <Select label="Actor" value={filters.actorType || ""} onChange={(event) => updateFilter("actorType", event.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(ACTOR_TYPE_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { sm: 180 } }}>
            <InputLabel>Período</InputLabel>
            <Select label="Período" value={filters.period || "7d"} onChange={(event) => updateFilter("period", event.target.value)}>
              {Object.entries(PERIODS).map(([key, period]) => <MenuItem key={key} value={key}>{period.label}</MenuItem>)}
            </Select>
          </FormControl>
        </ResponsiveListControls>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.35, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: 18, sm: 24 } }}>Historial</Typography>
            <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{pagination.total || 0} eventos relevantes</Typography>
          </Box>
          {listLoading && groupedItems.length ? <WebProfitSpinner size={24} /> : null}
        </Stack>
        {listError ? <Alert severity="error" sx={{ mb: 1.2 }} action={<Button color="inherit" size="small" onClick={loadList}>Reintentar</Button>}>{listError}</Alert> : null}
        {listLoading && !groupedItems.length ? (
          <DashboardLoadingState label="Cargando actividad…" minHeight={260} />
        ) : (
        <Stack spacing={1}>
          {groupedItems.map((group) => {
            const event = group.latest;
            const destination = activityEntityDestination(event, { projectBase, itemPath, projectKey });
            const entityLabel = String(event?.entity?.label || "").trim();
            return (
              <Box component="details" key={group.key} sx={{ p: { xs: 1.05, sm: 1.5 }, borderRadius: 3, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.05)", "&[open] .event-detail-chevron": { transform: "rotate(180deg)" } }}>
                <Stack component="summary" direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start" sx={{ minHeight: 46, cursor: "pointer", listStyle: "none", "&::-webkit-details-marker": { display: "none" } }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.25 }}>{activityDisplayTitle(event)}</Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: .28, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{activityActorLabel(event)} · {dateTime(event.occurredAt)}</Typography>
                    <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap sx={{ mt: .65 }}>
                      <Chip size="small" label={CATEGORY_LABELS[event.category] || event.category || "Actividad"} />
                      <Chip size="small" variant="outlined" label={sourceLabel(event.source)} />
                      {group.count > 1 ? <Chip size="small" color="primary" variant="outlined" label={`${group.count} acciones similares`} /> : null}
                    </Stack>
                  </Box>
                  <Typography className="event-detail-chevron" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", lineHeight: 1, transition: "transform .18s ease" }}>⌄</Typography>
                </Stack>

                {activityDisplayDescription(event) ? <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.64)", mt: .8, lineHeight: 1.5 }}>{activityDisplayDescription(event)}</Typography> : null}
                {entityLabel ? <Typography variant="body2" sx={{ mt: .7, color: BRAND.dark, fontWeight: 850 }}>Relacionado: {entityLabel}</Typography> : null}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={.8} alignItems={{ sm: "center" }} sx={{ mt: .9 }}>
                  {destination ? (
                    <Button size="small" variant="outlined" endIcon={<OpenInNewRoundedIcon />} onClick={(eventClick) => { eventClick.preventDefault(); navigate(destination.path); }} sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, textTransform: "none", fontWeight: 900 }}>
                      {destination.label}
                    </Button>
                  ) : null}
                  <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Tipo interno: {event.action || "sin código"}</Typography>
                </Stack>

                {group.count > 1 ? (
                  <Stack spacing={.35} sx={{ mt: 1, pt: .8, borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
                    <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 900 }}>Registros originales de este grupo</Typography>
                    {group.events.map((raw) => <Typography key={raw.id || raw._id} variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{dateTime(raw.occurredAt)} · {activityDisplayTitle(raw)}</Typography>)}
                  </Stack>
                ) : null}
              </Box>
            );
          })}
          {!listLoading && !groupedItems.length && !listError ? <Alert severity="info">No hay actividad relevante para estos filtros. Probá otro período o limpiá los filtros.</Alert> : null}
        </Stack>
        )}
        {pagination.pages > 1 ? <Stack alignItems="center" sx={{ mt: 2 }}><Pagination size="small" page={pagination.page || page} count={pagination.pages} onChange={(_event, value) => setPage(value)} color="primary" /></Stack> : null}
      </Paper>
    </Stack>
  );
}
